import { formatDateTime } from '@/lib/formatDate';
import { useState, useEffect } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { Pencil } from 'lucide-react';
import { getAccount, type AdmissionAccount } from '@/lib/ipdApi';
import { StatusChip } from '@/components/ui/status-chip';
import { admissionStatus, bedLabel } from '@/components/IPD/admissionStatus';

// ─── Status ────────────────────────────────────────────────────────────────

export const ADMISSION_STATUSES = ['admitted', 'discharged', 'lama', 'absconded', 'referred_out', 'expired'] as const;
export const DISCHARGE_STATUSES = ADMISSION_STATUSES.filter(s => s !== 'admitted');

export const STATUS_LABELS: Record<string, string> = {
  referred_out: 'Referred out',
  lama: 'LAMA (left against medical advice)',
  expired: 'Deceased',
};

export function StatusBadge({ status }: { status: string }) {
  const { tone, label } = admissionStatus(status);
  return <StatusChip tone={tone}>{label}</StatusChip>;
}

const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;

/** One fact in the admission line: small label above, value below */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="flex items-center gap-1.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

// ─── Admission line (under the patient banner, shared across all tabs) ────────

interface AdmissionSummaryProps {
  admission: any;
  canWrite: boolean;
  onDatesClick: () => void;
}

export function AdmissionSummary({ admission, canWrite, onDatesClick }: AdmissionSummaryProps) {
  const a = admission;
  const stayDays = a?.admission_date
    ? differenceInCalendarDays(a.discharge_date ? new Date(a.discharge_date) : new Date(), new Date(a.admission_date))
    : null;

  return (
    <dl className="flex flex-wrap items-start gap-x-8 gap-y-3">
      <Fact label="Status">
        <StatusBadge status={a?.status ?? ''} />
        {a?.status === 'admitted' && a?.discharge_advised_at && (
          <span title={`Advised${a.discharge_advised_by_name ? ` by ${a.discharge_advised_by_name}` : ''} · ${formatDateTime(a.discharge_advised_at)}`}>
            <StatusChip tone="warning">Discharge advised</StatusChip>
          </span>
        )}
        {!!a?.is_mlc && (
          <span title={a.mlc_number ? `MLC no. ${a.mlc_number}` : 'Medico-legal case'}><StatusChip tone="alert">MLC</StatusChip></span>
        )}
      </Fact>
      <Fact label="Ward / bed">
        <span className="truncate">{a?.ward_name ?? '—'}{a?.bed_number ? <> · {bedLabel(a.bed_number)}</> : null}</span>
      </Fact>
      <Fact label="Admitted">
        <span className="tabular-nums whitespace-nowrap">{a?.admission_date ? formatDateTime(a.admission_date) : '—'}</span>
        {canWrite && (
          <button type="button" onClick={onDatesClick} aria-label="Edit admission and discharge dates" title="Edit dates"
            className="touch-target text-muted-foreground hover:text-foreground">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </Fact>
      {a?.discharge_date && (
        <Fact label="Discharged">
          <span className="tabular-nums whitespace-nowrap">{formatDateTime(a.discharge_date)}</span>
        </Fact>
      )}
      {stayDays != null && (
        <Fact label="Stay">
          <span className="tabular-nums whitespace-nowrap">{stayDays === 0 ? (a?.discharge_date ? 'Same day' : 'Admitted today') : `${stayDays} day${stayDays === 1 ? '' : 's'}`}</span>
        </Fact>
      )}
      <Fact label="Doctor"><span className="truncate">{a?.admitting_doctor_name || '—'}</span></Fact>
    </dl>
  );
}

// ─── Bill figures (shared across all tabs) ────────────────────────────────────

export function BillingTotalsBar({ admission, refreshKey }: { admission: any; refreshKey?: number }) {
  const [account, setAccount] = useState<AdmissionAccount | null>(null);

  useEffect(() => {
    getAccount(admission.id).then(setAccount).catch(console.error);
  }, [admission.id, refreshKey]);

  const show = (n: number | undefined) => (account ? rupees(Number(n)) : '—');
  const final = account?.bill_status === 'final';

  return (
    <dl aria-label="Bill" className="flex items-start gap-x-6">
      <div className="text-right">
        <dt className="text-xs text-muted-foreground">{final ? 'Bill' : 'Bill so far'}</dt>
        <dd className="text-sm font-mono tabular-nums text-foreground">{show(account?.bill_total)}</dd>
      </div>
      {!!account?.paid && (
        <div className="text-right">
          <dt className="text-xs text-muted-foreground">Paid</dt>
          <dd className="text-sm font-mono tabular-nums text-foreground">{show(account.paid)}</dd>
        </div>
      )}
      {!!account?.advance_available && (
        <div className="text-right">
          <dt className="text-xs text-muted-foreground">Deposit</dt>
          <dd className="text-sm font-mono tabular-nums text-foreground">{show(account.advance_available)}</dd>
        </div>
      )}
      <div className="text-right">
        <dt className="text-xs text-muted-foreground">{final ? 'Due' : 'To collect'}</dt>
        <dd className="text-sm font-mono tabular-nums">
          {account && account.to_collect > 0
            ? <span className="rounded bg-highlight px-1.5 font-semibold text-highlight-foreground">{rupees(account.to_collect)}</span>
            : <span className="text-muted-foreground">{show(0)}</span>}
        </dd>
      </div>
    </dl>
  );
}
